import { memo, useEffect, useCallback, useMemo, useReducer } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { Advice, Button, Popover, Text, useTheme } from '@Sage/hoi-poi-ui';
import { PATH_IMAGE } from 'constants/Environment';
import { ACTIVITIES, OPPORTUNITIES, COMPANIES } from 'constants/Entities';
import Context from 'managers/Context';
import { getLiteral, getLiteralWithParametersHtml } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { getDistanceInMeters } from 'utils/map';
import { EntityModalActions, ActivitiesActions } from 'actions';
import { WidgetLayout, WidgetContentLayout } from 'containers/components/widgets/Layouts';
import { MapDisplay, AddressDisplay, LocationHeader, CheckInOutOfRangeModal } from './components';
import { parseBooleanForBackend } from '@Sage/ssm-utils/backendScalars';
import { logEvent } from 'utils/tracking';
import UtilFormat from 'utils/UtilFormat';
import {
    ACTIVITY_TYPE_CHECKIN,
    ACTIVITY_ID_TYPE_CHECKIN_COMPANY,
    ACTIVITY_ID_TYPE_CHECKIN_OPPORTUNITY,
} from 'models/ActivityModel';
import { publish } from 'lib/EventBuser';
import { ENTITY_MODAL_UPDATE } from 'lib/events';

import './styles.scss';

const WIDGET_HEIGHTS = { REGULAR: 190, FULL: 240 };

// Reducer action types
const LOCATION_ACTIONS = {
    SET_LOADING_DISTANCE: 'SET_LOADING_DISTANCE',
    SET_DISTANCE: 'SET_DISTANCE',
    SET_GEO_PERMISSION: 'SET_GEO_PERMISSION',
    SET_COORDINATES: 'SET_COORDINATES',
    SET_SHOW_CHECK_IN_OUT_OF_RANGE_MODAL: 'SET_SHOW_CHECK_IN_OUT_OF_RANGE_MODAL',
};

// Initial state for the reducer
const initialLocationState = {
    loadingDistance: false,
    distance: null,
    geoPermission: null,
    coordinates: null,
    showCheckInOutOfRangeModal: false,
};

// Reducer function
const locationReducer = (state, action) => {
    switch (action.type) {
        case LOCATION_ACTIONS.SET_LOADING_DISTANCE:
            return { ...state, loadingDistance: action.payload };
        case LOCATION_ACTIONS.SET_DISTANCE:
            return { ...state, distance: action.payload };
        case LOCATION_ACTIONS.SET_GEO_PERMISSION:
            return { ...state, geoPermission: action.payload };
        case LOCATION_ACTIONS.SET_COORDINATES:
            return { ...state, coordinates: action.payload };
        case LOCATION_ACTIONS.SET_SHOW_CHECK_IN_OUT_OF_RANGE_MODAL:
            return { ...state, showCheckInOutOfRangeModal: action.payload };
        default:
            return state;
    }
};

const EntityLocationWidget = memo(
    ({
        entity,
        entityData,
        allowCheckIn,
        allowCheckOut,
        notGeoLocalizedEntityLiteral,
        setSize,
    }) => {
        const dispatch = useDispatch();
        const theme = useTheme();

        // ========================================
        // REDUX STATE & SELECTORS
        // ========================================
        const permissions = useSelector((state) => state.config.permission);
        const userData = useSelector((state) => state.config.userData);
        const checkInInfo = useSelector(
            (state) => state.activities.checkIn?.[entity.entity] || null,
        );

        // Local state management
        const [
            { loadingDistance, distance, geoPermission, coordinates, showCheckInOutOfRangeModal },
            dispatchLocation,
        ] = useReducer(locationReducer, initialLocationState);

        // Derived state from Redux
        const show_checkin_form_when_checkin = permissions.show_checkin_form_when_checkin;
        const show_checkin_form_when_checkout = permissions.show_checkin_form_when_checkout;
        const maxCheckInDistanceMeters = permissions.maxCheckInDistanceMeters;
        const allowOutOfRangeCheckIn = permissions.allowOutOfRangeCheckIn;
        const userName = `${userData.nombre} ${userData.apellidos}`;
        const userId = userData.idUsuario;
        const pendingCheckOut = checkInInfo?.pendingCheckOut || '';
        const lastCheckIn = checkInInfo?.lastCheckIn || null;
        const checkInIntervalTime = checkInInfo?.checkInIntervalTime || 0;
        const isButtonLoading = checkInInfo?.isButtonLoading || false;
        const isButtonDisabled = checkInInfo?.isButtonDisabled || false;

        // Memoized derived values
        const isGeoLocated = useMemo(() => entityData.isGeoLocated, [entityData.isGeoLocated]);

        const maxDistance = useMemo(() => {
            return maxCheckInDistanceMeters ? parseInt(maxCheckInDistanceMeters, 10) : 0;
        }, [maxCheckInDistanceMeters]);

        const entityCoordinates = useMemo(() => {
            if (!entityData.latitude || !entityData.longitude) return null;
            return {
                lat: parseFloat(entityData.latitude),
                lon: parseFloat(entityData.longitude),
            };
        }, [entityData.latitude, entityData.longitude]);

        // Redux action creators
        const {
            modalInit,
            addPendingCheckOut,
            removePendingCheckOut,
            changeCheckInButtonLoading,
            getLastCheckInForLocationWidget,
            clearCheckInInterval,
        } = useMemo(
            () => ({
                modalInit: (payload) => {
                    dispatch(EntityModalActions.init(payload));
                },
                addPendingCheckOut: (entity, entityId) => {
                    dispatch(ActivitiesActions.addPendingCheckOut(entity, entityId));
                },
                removePendingCheckOut: (entity) => {
                    dispatch(ActivitiesActions.removePendingCheckOut(entity));
                },
                changeCheckInButtonLoading: (entity, isLoading) => {
                    dispatch(ActivitiesActions.changeCheckInButtonLoading(entity, isLoading));
                },
                getLastCheckInForLocationWidget: (payload) => {
                    dispatch(ActivitiesActions.getLastCheckInForLocationWidget(payload));
                },
                clearCheckInInterval: () => {
                    dispatch(ActivitiesActions.clearCheckInInterval());
                },
            }),
            [dispatch],
        );

        // ========================================
        // GEOLOCATION LOGIC
        // ========================================

        const getGeoPermission = useCallback(() => {
            if (!/(safari)/i.test(navigator.userAgent)) {
                return navigator.permissions
                    .query({ name: 'geolocation' })
                    .then((result) => {
                        return dispatchLocation({
                            type: LOCATION_ACTIONS.SET_GEO_PERMISSION,
                            payload: result?.state,
                        });
                    })
                    .catch((err) => console.error('ERROR: ', err));
            } else {
                return dispatchLocation({
                    type: LOCATION_ACTIONS.SET_GEO_PERMISSION,
                    payload: 'granted',
                });
            }
        }, []);

        const getCurrentLocation = useCallback(() => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (position) {
                        let distance = getDistanceInMeters(
                            position.coords.latitude,
                            position.coords.longitude,
                            entityData.latitude,
                            entityData.longitude,
                        );
                        dispatchLocation({
                            type: LOCATION_ACTIONS.SET_LOADING_DISTANCE,
                            payload: false,
                        });
                        dispatchLocation({
                            type: LOCATION_ACTIONS.SET_DISTANCE,
                            payload: distance,
                        });
                        dispatchLocation({
                            type: LOCATION_ACTIONS.SET_COORDINATES,
                            payload: {
                                lat: position.coords.latitude,
                                lon: position.coords.longitude,
                            },
                        });
                    } else {
                        dispatchLocation({
                            type: LOCATION_ACTIONS.SET_LOADING_DISTANCE,
                            payload: false,
                        });
                    }
                },
                (error) => {
                    console.error('error', error);
                    dispatchLocation({
                        type: LOCATION_ACTIONS.SET_GEO_PERMISSION,
                        payload: 'denied',
                    });
                    dispatchLocation({
                        type: LOCATION_ACTIONS.SET_LOADING_DISTANCE,
                        payload: false,
                    });
                },
            );
        }, [entityData.latitude, entityData.longitude]);

        const geoAccuracy = useMemo(() => {
            const accuracyMap = {
                [COMPANIES.entity]: entityData.companyGeoAccuracy,
                [OPPORTUNITIES.entity]: entityData.opportunityGeoAccuracy,
            };
            const accuracy = accuracyMap[entity.entity];
            return accuracy ? parseInt(accuracy, 10) : null;
        }, [entity, entityData.companyGeoAccuracy, entityData.opportunityGeoAccuracy]);

        // ========================================
        // CHECK-IN/CHECK-OUT LOGIC
        // ========================================

        const getIDTipoCheckIn = useMemo(() => {
            const checkInTypeMap = {
                [COMPANIES]: ACTIVITY_ID_TYPE_CHECKIN_COMPANY,
                [OPPORTUNITIES]: ACTIVITY_ID_TYPE_CHECKIN_OPPORTUNITY,
            };
            return checkInTypeMap[entity] || ACTIVITY_ID_TYPE_CHECKIN_OPPORTUNITY;
        }, [entity]);

        const createCheckIn = useCallback(() => {
            const dataCreators = {
                [COMPANIES]: () => ({
                    idEmpresa: entityData.id,
                }),
                [OPPORTUNITIES]: () => {
                    const data = { idExpediente: entityData.id };
                    if (entityData.companyId) data.idEmpresa = entityData.companyId;
                    return data;
                },
            };

            const data = dataCreators[entity]?.();

            Context.entityManager
                .getEntitiesManager(ACTIVITIES)
                .createCheckIn(data)
                .then((result) => {
                    changeCheckInButtonLoading(entity, false);
                    addPendingCheckOut(entity, entityData.id);
                    successToast({
                        text: getLiteral('label_checkin_action_completed_notification'),
                    });
                    getLastCheckInForLocationWidget({
                        entityData,
                        allowCheckOut,
                        entity,
                    });
                    publish(`${ENTITY_MODAL_UPDATE}--${ACTIVITIES.entity}`);
                })
                .catch(() => {
                    changeCheckInButtonLoading(entity, false);
                    errorToast({ text: getLiteral('error_checkin_action_completed_notification') });
                });
        }, [
            entity,
            entityData,
            allowCheckOut,
            getLastCheckInForLocationWidget,
            changeCheckInButtonLoading,
            addPendingCheckOut,
        ]);

        const makeCheckOutOfCheckIn = useCallback(
            (idType) => {
                Context.entityManager.getEntitiesManager(ACTIVITIES).makeCheckOutOfCheckIn(
                    idType,
                    lastCheckIn.Id,
                    () => {
                        changeCheckInButtonLoading(entity, false);
                        removePendingCheckOut(entity);
                        successToast({
                            text: getLiteral('label_checkout_action_completed_notification'),
                        });
                        publish(`${ENTITY_MODAL_UPDATE}--${ACTIVITIES.entity}`);
                    },
                    () => {
                        changeCheckInButtonLoading(entity, false);
                        errorToast({
                            text: getLiteral('error_checkin_action_completed_notification'),
                        });
                    },
                );
            },
            [entity, lastCheckIn?.Id, changeCheckInButtonLoading, removePendingCheckOut],
        );

        const doCheckIn = useCallback(() => {
            if (allowCheckOut && pendingCheckOut && lastCheckIn) {
                // we can't do a CheckIn if we have yet a CheckIn in progress
                // in this case, force a CheckOut
                if (show_checkin_form_when_checkout) {
                    const lat = lastCheckIn?.Latitude?.replace(',', '.') || '';
                    const lon = lastCheckIn?.Longitude?.replace(',', '.') || '';
                    modalInit({
                        entity: ACTIVITIES,
                        id: lastCheckIn.Id,
                        data: {
                            lat,
                            lon,
                            dtCheckOutDate: moment().utc().format(),
                        },
                        labels: {
                            title: getLiteral('label_checkout'),
                            success: getLiteral('label_checkout_action_completed_notification'),
                            error: getLiteral('label_failed_insert_contact_salesforce'),
                        },
                        hideDelete: true,
                    });
                    return;
                } else {
                    changeCheckInButtonLoading(entity, true);
                    makeCheckOutOfCheckIn(lastCheckIn.IDTipoCheckin);
                }
            }

            if (show_checkin_form_when_checkin) {
                const detailData = {};
                if (entity?.entity === COMPANIES.entity && entityData?.id) {
                    detailData.idEmpresa = {
                        label: entityData?.name || '',
                        value: entityData?.id || '',
                    };
                }

                if (entity?.entity === OPPORTUNITIES.entity && entityData?.id) {
                    detailData.idExpediente = {
                        label: entityData?.name || '',
                        value: entityData?.id || '',
                    };
                    if (entityData?.companyId) {
                        detailData.idEmpresa = {
                            label: entityData?.companyDescription || '',
                            value: entityData?.companyId,
                        };
                    }
                }

                modalInit({
                    entity: ACTIVITIES,
                    data: {
                        _idComercial: {
                            label: userName,
                            value: userId,
                        },
                        isGeoCoded: parseBooleanForBackend(entityData?.isGeoLocated),
                        lat: entityData?.latitude?.toString() || '',
                        lon: entityData?.longitude?.toString() || '',
                        activityType: ACTIVITY_TYPE_CHECKIN,
                        IDTipocheckin: getIDTipoCheckIn,
                        isCheckin: '1',
                        ...detailData,
                    },
                    labels: {
                        title: getLiteral('title_add_activity'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                    },
                    hideDelete: true,
                });
            } else {
                changeCheckInButtonLoading(entity, true);
                createCheckIn();
            }

            logEvent({
                event: entity.trueName,
                functionality: 'checkIn',
            });
        }, [
            allowCheckOut,
            createCheckIn,
            entity,
            entityData,
            makeCheckOutOfCheckIn,
            show_checkin_form_when_checkin,
            show_checkin_form_when_checkout,
            modalInit,
            userId,
            userName,
            changeCheckInButtonLoading,
            pendingCheckOut,
            lastCheckIn,
            getIDTipoCheckIn,
        ]);

        const doCheckOut = useCallback(() => {
            if (show_checkin_form_when_checkout) {
                const lat = lastCheckIn?.Latitude?.replace(',', '.') || '';
                const lon = lastCheckIn?.Longitude?.replace(',', '.') || '';
                modalInit({
                    entity: ACTIVITIES,
                    id: lastCheckIn.Id,
                    data: {
                        lat,
                        lon,
                        dtCheckOutDate: moment().utc().format(),
                    },
                    labels: {
                        title: getLiteral('label_checkout'),
                        success: getLiteral('label_checkout_action_completed_notification'),
                        error: getLiteral('label_failed_insert_contact_salesforce'),
                    },
                    hideDelete: true,
                });
            } else {
                changeCheckInButtonLoading(entity, true);
                makeCheckOutOfCheckIn(lastCheckIn.IDTipoCheckin, true);
            }
        }, [
            entity,
            show_checkin_form_when_checkout,
            modalInit,
            changeCheckInButtonLoading,
            makeCheckOutOfCheckIn,
            lastCheckIn,
        ]);

        const doCheckInOutOfRange = useCallback(() => {
            dispatchLocation({
                type: LOCATION_ACTIONS.SET_SHOW_CHECK_IN_OUT_OF_RANGE_MODAL,
                payload: true,
            });
        }, []);

        const closeCheckInOutOfRangeModal = useCallback(() => {
            dispatchLocation({
                type: LOCATION_ACTIONS.SET_SHOW_CHECK_IN_OUT_OF_RANGE_MODAL,
                payload: false,
            });
        }, []);

        const onConfirmCheckInOutOfRange = useCallback(() => {
            closeCheckInOutOfRangeModal();
            const detailData = {};
            if (entity?.entity === COMPANIES.entity && entityData?.id) {
                detailData.idEmpresa = {
                    label: entityData?.name || '',
                    value: entityData?.id || '',
                };
            }

            if (entity?.entity === OPPORTUNITIES.entity && entityData?.id) {
                detailData.idExpediente = {
                    label: entityData?.name || '',
                    value: entityData?.id || '',
                };
                if (entityData?.companyId) {
                    detailData.idEmpresa = {
                        label: entityData?.companyDescription || '',
                        value: entityData?.companyId,
                    };
                }
            }
            modalInit({
                entity: ACTIVITIES,
                data: {
                    _idComercial: {
                        label: userName,
                        value: userId,
                    },
                    lat: coordinates.lat,
                    lon: coordinates.lon,
                    activityType: ACTIVITY_TYPE_CHECKIN,
                    outOfRangeCheckin: 'True',
                    ...detailData,
                },
                labels: {
                    title: getLiteral('title_add_activity'),
                    success: getLiteral('succes_entitycreatedsuccessfully'),
                    error: getLiteral('label_failed_create'),
                },
                hideDelete: true,
            });
        }, [
            coordinates,
            entity,
            modalInit,
            entityData,
            closeCheckInOutOfRangeModal,
            userName,
            userId,
        ]);

        // ========================================
        // UI & BUTTON LOGIC
        // ========================================

        const createButton = useCallback(({ isDisabled, isLoading, onClick, text }) => {
            return (
                <Button
                    type="secondary"
                    size="medium"
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onClick={onClick}
                >
                    {text}
                </Button>
            );
        }, []);

        const { buttonDisabled, checkInButton, checkOutButton, checkInOutOfRangeButton } =
            useMemo(() => {
                const disabledText =
                    checkInIntervalTime > 0
                        ? `${Math.floor(checkInIntervalTime / 60)}:${(checkInIntervalTime % 60).toString().padStart(2, '0')}`
                        : getLiteral('action_checkin');

                return {
                    buttonDisabled: createButton({
                        text: disabledText,
                        isDisabled: true,
                        isLoading: false,
                        onClick: undefined,
                    }),
                    checkInButton: createButton({
                        text: getLiteral('label_checkin'),
                        isDisabled: false,
                        isLoading: isButtonLoading,
                        onClick: doCheckIn,
                    }),
                    checkOutButton: createButton({
                        text: getLiteral('label_checkout'),
                        isDisabled: false,
                        isLoading: isButtonLoading,
                        onClick: doCheckOut,
                    }),
                    checkInOutOfRangeButton: createButton({
                        text: getLiteral('action_checkin_outofrange'),
                        isDisabled: false,
                        isLoading: false,
                        onClick: doCheckInOutOfRange,
                    }),
                };
            }, [
                checkInIntervalTime,
                isButtonLoading,
                doCheckIn,
                doCheckOut,
                doCheckInOutOfRange,
                createButton,
            ]);

        const buttonAction = useMemo(() => {
            // Don't show check-in button if there's no geolocation permission
            if (geoPermission !== 'granted') return null;

            if (loadingDistance || !distance || !isGeoLocated) return null;

            const isWithinRange = maxCheckInDistanceMeters && distance <= maxDistance;
            const distanceWithUnit = UtilFormat.getDistanceWithUnit(distance);

            const popoverContent = (
                <div className="entity-location-widget__out-of-range-popoverContent">
                    <Text type="subtitle" medium color={theme.colors.textLight.primary}>
                        {getLiteral('label_checkin_out_of_the_range')}
                    </Text>
                    <Advice type="semanticInfo" showIcon showCollapse={false}>
                        {getLiteral('label_checkin_out_of_the_range_info')}
                    </Advice>
                    <Text
                        className="entity-location-widget__out-of-range-popoverContent__footer"
                        color={theme.colors.textLight.secondary}
                    >
                        {getLiteralWithParametersHtml(
                            'label_checkin_out_of_the_range_for',
                            [entityData?.name, distanceWithUnit],
                            (text) => (
                                <strong>{text}</strong>
                            ),
                        )}
                    </Text>
                </div>
            );

            if (isWithinRange) {
                if (isButtonDisabled || checkInIntervalTime > 0) return buttonDisabled;
                if (pendingCheckOut) {
                    return allowCheckOut ? checkOutButton : null;
                } else {
                    return allowCheckIn ? checkInButton : null;
                }
            } else {
                return allowOutOfRangeCheckIn ? (
                    checkInOutOfRangeButton
                ) : (
                    <Popover
                        className="entity-location-widget__out-of-range-popover"
                        placement="top"
                        trigger={['hover']}
                        content={popoverContent}
                    >
                        {buttonDisabled}
                    </Popover>
                );
            }
        }, [
            geoPermission,
            distance,
            loadingDistance,
            isGeoLocated,
            maxDistance,
            maxCheckInDistanceMeters,
            isButtonDisabled,
            checkInIntervalTime,
            pendingCheckOut,
            allowCheckIn,
            allowCheckOut,
            allowOutOfRangeCheckIn,
            buttonDisabled,
            checkInButton,
            checkOutButton,
            checkInOutOfRangeButton,
            theme,
            entityData?.name,
        ]);

        const address = useMemo(() => {
            return entityData.fullAddress && entityData.fullAddress.length > 0
                ? entityData.fullAddress
                : getLiteral('label_without_address');
        }, [entityData.fullAddress]);

        // ========================================
        // EFFECTS
        // ========================================

        useEffect(() => {
            getGeoPermission();
        }, [getGeoPermission]);

        useEffect(() => {
            const targetHeight = isGeoLocated ? WIDGET_HEIGHTS.FULL : WIDGET_HEIGHTS.REGULAR;
            setSize({ height: targetHeight }, false, true);
        }, [setSize, isGeoLocated]);

        useEffect(() => {
            if (!isGeoLocated) return;

            dispatchLocation({ type: LOCATION_ACTIONS.SET_LOADING_DISTANCE, payload: true });
            dispatchLocation({ type: LOCATION_ACTIONS.SET_DISTANCE, payload: null });
            getCurrentLocation();

            // Only fetch check-in info if user has check-in permissions
            if (allowCheckIn || allowOutOfRangeCheckIn) {
                getLastCheckInForLocationWidget({
                    entityData,
                    allowCheckOut,
                    entity,
                });
            }

            return () => {
                if (allowCheckIn || allowOutOfRangeCheckIn) {
                    clearCheckInInterval();
                }
            };
        }, [
            allowCheckIn,
            allowOutOfRangeCheckIn,
            isGeoLocated,
            entityData,
            getCurrentLocation,
            getLastCheckInForLocationWidget,
            allowCheckOut,
            entity,
            clearCheckInInterval,
        ]);

        // ========================================
        // RENDER
        // ========================================

        return (
            <WidgetLayout className="entity-location-widget" type="location" data={entityData}>
                <LocationHeader geoLastUpdated={entityData.geoLastUpdated} />
                <WidgetContentLayout
                    hasHeader
                    className={classNames('entity-location-widget-content', {
                        'entity-location-widget-content--with-actions': !!buttonAction,
                    })}
                    isColumn={false}
                >
                    <MapDisplay
                        isGeoLocated={isGeoLocated}
                        latitude={entityData.latitude}
                        longitude={entityData.longitude}
                    />
                    <AddressDisplay
                        address={address}
                        loadingDistance={loadingDistance}
                        distance={distance}
                        geoPermission={geoPermission}
                        coordinates={coordinates || entityCoordinates}
                        notGeoLocalizedEntityLiteral={notGeoLocalizedEntityLiteral}
                        entity={entity}
                        geoAccuracy={geoAccuracy}
                    />
                    {!!buttonAction && (
                        <div className="entity-location-widget__actions">{buttonAction}</div>
                    )}
                    <CheckInOutOfRangeModal
                        isOpen={showCheckInOutOfRangeModal}
                        onConfirm={onConfirmCheckInOutOfRange}
                        onClose={closeCheckInOutOfRangeModal}
                    />
                </WidgetContentLayout>
            </WidgetLayout>
        );
    },
);

EntityLocationWidget.propTypes = {
    notGeoLocalizedEntityLiteral: PropTypes.string,
    entity: PropTypes.object.isRequired,
    entityData: PropTypes.object.isRequired,
    allowCheckIn: PropTypes.bool,
    allowCheckOut: PropTypes.bool,
    setSize: PropTypes.func.isRequired,
};

export default EntityLocationWidget;
